import { useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Bell, ChevronsUpDown, Languages, LogOut, MonitorSmartphone, PanelLeftClose, PanelLeftOpen, Plus, Search, ShieldCheck, Sparkles, Timer } from "lucide-react";
import { api, currentCompany, type S } from "@/shared/api/client";
import { tabOf, type AppInfo } from "@/shared/apps";
import { companyName, errorMessage, joinList, pickLanguage } from "@/shared/i18n";
import { useCan, useMe } from "@/shared/lib/session";
import { date, dateTime, relative } from "@/shared/lib/format";
import { flushOutbox } from "@/shared/lib/outbox";
import { useAction } from "@/shared/lib/query";
import { useInstall, useOnline } from "@/shared/lib/pwa";
import { POP_END, POP_START, usePopover } from "@/shared/ui/popover";
import { Avatar } from "@/shared/ui/Avatar";
import { PlaceContext } from "@/shared/ui/Page";
import { applyBrand } from "@/shared/lib/brand";
import { BrandIcon, BrandMark } from "@/shared/ui/BrandMark";
import { DemoRoleSwitch } from "./Demo";
import { toggleApp, useAppNeeds, useSaveApps } from "./AppCards";
import { AppStartProvider, useAppStart } from "./AppStart";
import { apps, hrefParts, useAppOn } from "./apps";
import { pageOf } from "./areas";
import { CommandPalette } from "./CommandPalette";
import { AnnouncementBanners, useWhatsNew, WhatsNewDialog } from "./FromClientOS";
import { useCreates } from "./creates";
import { useReportPages } from "./usage";
import { createIcon } from "./QuickActions";
import { BottomBar, MenuDrawer, NewSheet } from "./MobileNav";
import { Sidebar, useHere } from "./Sidebar";

/** An open/closed choice that survives a reload; storage can be unavailable (private windows), so it only ever falls back. */
function useRemembered(key: string, initial: boolean): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(key) ? localStorage.getItem(key) === "1" : initial; } catch { return initial; }
  });
  const set = (v: boolean) => {
    setOn(v);
    try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* the choice just isn't kept */ }
  };
  return [on, set];
}

/** A page of an app that's switched off (an old link, a bookmark): what the app does, and one click to switch it on. */
function AppOff({ app }: { app: AppInfo }) {
  const { t } = useTranslation();
  const can = useCan();
  const { data: me } = useMe();
  const { save, busy } = useSaveApps();
  const needs = useAppNeeds();
  const start = useAppStart();
  const name = t(`apps.${app.key}.name`);
  const list = (keys: string[]) => joinList(keys.map((k) => t(`apps.${k}.name`)));
  const turnOn = async () => {
    try {
      await save(toggleApp((me?.tenant.modules ?? []).filter((m) => m !== "core"), app.key, needs));
      start(app.key);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  let action: ReactNode;
  if (app.shownWith) action = <p className="mt-4 text-[13px] text-ink-3">{t("apps.comesWith", { apps: list(app.shownWith) })}</p>;
  else if (!me?.tenant.plan_modules?.includes(app.key)) action = (
    <>
      <p className="mt-4 text-[13px] text-ink-3">{t("apps.notInPlan")}</p>
      {can("core.billing.manage") && <Link to="/settings/subscription" className="btn-primary mt-4">{t("apps.seePlans")}</Link>}
    </>
  );
  else if (!can("core.apps.manage")) action = <p className="mt-4 text-[13px] text-ink-3">{t("apps.askAdmin")}</p>;
  else action = (
    <>
      <button type="button" className="btn-primary mt-5" disabled={busy} onClick={turnOn}>{t("apps.turnOn", { app: name })}</button>
      {needs[app.key]?.length ? <p className="mt-2 text-[12px] text-ink-3">{t("apps.needs", { app: list(needs[app.key]) })}</p> : null}
    </>
  );
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-line-2 text-ink-2"><app.icon className="size-6" /></span>
      <h1 className="mt-4 text-[18px] font-semibold">{t("apps.offTitle", { app: name })}</h1>
      <p className="mt-1 text-ink-2">{t(`apps.${app.key}.description`)}</p>
      {action}
    </div>
  );
}

/** Everything one can create from the apps that are on: the six made most often, then the rest a click further. */
function NewMenu() {
  const { t } = useTranslation();
  const { open, setOpen, ref } = usePopover();
  const [all, setAll] = useState(false);
  // What's made in the area the user is in comes first (a delivery note on the deliveries page).
  const { areas, area } = useHere();
  const creates = useCreates(area && ((c) => {
    const [path, query = ""] = c.to.split("?");
    return pageOf(areas, path, Object.fromEntries(new URLSearchParams(query)))?.area.key === area.key;
  }));
  if (creates.length === 0) return null;
  const shown = all ? creates : creates.slice(0, 6);
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="btn-primary" aria-haspopup="menu" aria-expanded={open} onClick={() => { setOpen((o) => !o); setAll(false); }}>
        <Plus className="size-4" /> {t("create.title")}
      </button>
      {open && (
        <div role="menu" className={clsx(POP_END, "max-h-[70vh] w-64 overflow-y-auto rounded-xl p-1.5")}>
          {shown.map((c) => {
            const Icon = createIcon[c.label] ?? Plus;
            const { to, search } = hrefParts(c.to);
            return (
              <Link key={c.to} role="menuitem" to={to} search={search as never} onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-paper">
                <Icon className="size-4 shrink-0 text-ledger" aria-hidden />{t(c.label)}
              </Link>
            );
          })}
          {!all && creates.length > shown.length && (
            <button type="button" className="mt-1 w-full border-t border-line-2 px-2.5 pt-2 pb-1 text-start text-[13px] text-ink-3 hover:text-ink" onClick={() => setAll(true)}>
              {t("create.all", { count: creates.length })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** The page, or a note when the app it belongs to is switched off; it's told which area it's in. */
function Page({ onArea }: { onArea: () => void }) {
  const { t } = useTranslation();
  const { area, path } = useHere();
  const on = useAppOn();
  const off = apps.find((a) => tabOf(a, path) && !on(a));
  if (off) return <AppOff app={off} />;
  return (
    <PlaceContext.Provider value={{ area: area && t(`areas.${area.key}`), onArea: area && area.pages.length > 1 ? onArea : undefined }}>
      <Outlet />
    </PlaceContext.Provider>
  );
}

/** The company the user works in, at the top of the menu; with several companies it switches between them. */
function CompanySwitcher() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { open, setOpen, ref } = usePopover();
  if (!me) return null;
  const current = me.companies?.find((c) => c.id === me.current_company_id);
  const name = (c?: S["CompanyRef"]) => companyName(c);
  const tile = (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-ink text-[12px] font-bold text-white" aria-hidden>
      {name(current).replace(/^(شركة|مؤسسة|مكتب)\s+/, "").replace(/^ال/, "").charAt(0)}
    </span>
  );
  if (!me.companies || me.companies.length < 2) {
    return <div className="flex items-center gap-2.5 px-2.5 py-2 text-[13.5px] font-medium" title={name(current)}>{tile}<span className="truncate">{name(current)}</span></div>;
  }
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-start text-[13.5px] font-medium hover:bg-ink/[.04]"
        onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} title={t("shell.switchCompany")}>
        {tile}
        <span className="flex-1 truncate">{name(current)}</span>
        <ChevronsUpDown className="size-4 shrink-0 text-ink-3" />
      </button>
      {open && (
        <ul role="menu" className={clsx(POP_START, "w-64 rounded-[var(--radius-ctl)] py-1")}>
          {me.companies.map((c) => (
            <li key={c.id}>
              <button
                role="menuitem"
                type="button"
                className={clsx("w-full px-3 py-1.5 text-start hover:bg-paper", c.id === me.current_company_id && "font-medium text-ledger-dark")}
                onClick={async () => {
                  currentCompany.set(c.id);
                  setOpen(false);
                  await qc.resetQueries();
                }}
              >
                {name(c)}
                <span className="ms-2 text-[12px] text-ink-3">{c.base_currency}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationsBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { open, setOpen, ref } = usePopover();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<S["NotifList"]>("/v1/core/notifications"),
    refetchInterval: 60_000,
  });
  const unread = data?.unread ?? 0;
  const markAll = async () => {
    await api.post("/v1/core/notifications/read", { ids: [] });
    await qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="btn-ghost relative p-2" aria-label={t("shell.notifications")} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Bell className="size-4" />
        {unread > 0 && <span className="num absolute -top-0.5 -end-0.5 rounded-full bg-danger px-1 text-[10px] text-white">{unread}</span>}
      </button>
      {open && (
        <div className={clsx(POP_END, "w-80 rounded-[var(--radius-panel)]")}>
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="font-medium">{t("shell.notifications")}</span>
            {unread > 0 && <button type="button" className="text-[12px] text-ledger hover:underline" onClick={markAll}>{t("shell.markAllRead")}</button>}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {(data?.items ?? []).length === 0 && <li className="px-3 py-6 text-center text-ink-3">{t("shell.noNotifications")}</li>}
            {data?.items?.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={clsx("w-full border-b border-line-2 px-3 py-2 text-start hover:bg-paper", !n.read_at && "bg-ledger-soft/40")}
                  onClick={async () => {
                    setOpen(false);
                    if (!n.read_at) {
                      await api.post("/v1/core/notifications/read", { ids: [n.id] });
                      await qc.invalidateQueries({ queryKey: ["notifications"] });
                    }
                    if (n.link) void navigate({ to: n.link });
                  }}
                >
                  <div className="font-medium">{n.title}</div>
                  {n.body && <div className="line-clamp-2 text-[13px] text-ink-2">{n.body}</div>}
                  <div className="mt-0.5 text-[12px] text-ink-3">{relative(n.created_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TimerChip() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const can = useCan();
  const { data } = useQuery({
    queryKey: ["timer"],
    queryFn: () => api.get<S["ProjectsTimer"] | null>("/v1/projects/timer"),
    enabled: can("projects.time.create"),
    refetchInterval: 60_000,
  });
  const [, tick] = useState(0);
  useEffect(() => {
    if (!data) return;
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [data]);
  if (!data) return null;
  const mins = Math.max(0, Math.floor((Date.now() - new Date(data.started_at).getTime()) / 60000));
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-ledger/30 bg-ledger-soft py-0.5 ps-2.5 pe-1 text-[13px] text-ledger-dark">
      <Timer className="size-3.5" />
      <span className="max-w-32 truncate" title={data.project_name}>{data.project_name}</span>
      <span className="num">{Math.floor(mins / 60)}:{String(mins % 60).padStart(2, "0")}</span>
      <button
        type="button"
        className="rounded-full bg-ledger px-2 py-0.5 text-[12px] text-white hover:bg-ledger-dark"
        onClick={async () => {
          await api.post("/v1/projects/timer/stop");
          await qc.invalidateQueries({ queryKey: ["timer"] });
          await qc.invalidateQueries({ queryKey: ["/v1/projects/time-entries"] });
        }}
      >
        {t("shell.stopTimer")}
      </button>
    </div>
  );
}

function UserMenu() {
  const { data: me } = useMe();
  const { t, i18n } = useTranslation();
  const { open, setOpen, ref } = usePopover();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const install = useInstall();
  const news = useWhatsNew();
  const [reading, setReading] = useState(false);
  if (!me) return null;
  const unseen = news.data?.unseen ?? 0;
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="relative flex rounded-full" aria-haspopup="menu" aria-expanded={open} aria-label={t("nav.profile")} onClick={() => setOpen((o) => !o)}>
        <Avatar name={me.name} fileId={me.avatar_file_id} className="size-8 text-[13px]" />
        {unseen > 0 && <span className="absolute -top-0.5 -end-0.5 size-2.5 rounded-full border-2 border-surface bg-ledger" aria-hidden />}
      </button>
      {open && (
        <ul role="menu" className={clsx(POP_END, "w-56 rounded-[var(--radius-ctl)] py-1")}>
          <li className="border-b border-line-2">
            <button role="menuitem" type="button" className="flex w-full items-center gap-3 px-3 py-2.5 text-start hover:bg-paper"
              onClick={() => { setOpen(false); void navigate({ to: "/settings/profile" }); }}>
              <Avatar name={me.name} fileId={me.avatar_file_id} className="size-10 text-[15px]" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{me.name}</span>
                <span className="block truncate text-[12px] text-ink-3">{me.job_title || me.email}</span>
                <span className="block text-[12px] text-ledger">{t("profile.view")}</span>
              </span>
            </button>
          </li>
          <li>
            <button role="menuitem" type="button" className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-paper"
              onClick={() => { setOpen(false); void navigate({ to: "/settings/security" }); }}>
              <ShieldCheck className="size-4" /> {t("settings.security")}
            </button>
          </li>
          {(news.data?.items?.length ?? 0) > 0 && (
            <li>
              <button role="menuitem" type="button" className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-paper"
                onClick={() => { setOpen(false); setReading(true); }}>
                <Sparkles className="size-4" /> <span className="flex-1 text-start">{t("shell.whatsNew")}</span>
                {unseen > 0 && <span className="num rounded-full bg-ledger px-1.5 text-[11px] text-white">{unseen}</span>}
              </button>
            </li>
          )}
          <li>
            <button role="menuitem" type="button" className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-paper"
              onClick={() => { setOpen(false); const lng = i18n.language === "ar" ? "en" : "ar"; pickLanguage(lng); void api.put("/v1/me", { name: me.name, phone: me.phone, timezone: me.timezone, locale: lng }).catch(() => {}); }}>
              <Languages className="size-4" /> {t("shell.language")}
            </button>
          </li>
          {install.how && (
            <li>
              <button role="menuitem" type="button" className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-paper"
                onClick={() => { setOpen(false); if (install.how === "prompt") void install.install(); else toast.info(t("shell.installIOS"), { duration: 10_000 }); }}>
                <MonitorSmartphone className="size-4" /> {t("shell.install")}
              </button>
            </li>
          )}
          <li>
            <button role="menuitem" type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-danger hover:bg-danger-soft"
              onClick={async () => {
                await api.post("/v1/auth/logout").catch(() => {});
                qc.clear();
                void navigate({ to: "/login" });
              }}>
              <LogOut className="size-4" /> {t("nav.signOut")}
            </button>
          </li>
        </ul>
      )}
      {reading && <WhatsNewDialog onClose={() => setReading(false)} />}
    </div>
  );
}

function VerifyBanner({ email }: { email: string }) {
  const { t } = useTranslation();
  const resend = useAction(() => api.post("/v1/auth/verify-email/resend"), { success: t("shell.verifySent") });
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-info-soft px-4 py-1.5 text-center text-[13px] text-info">
      {t("shell.verifyEmail", { email })}
      <button type="button" className="font-medium underline" disabled={resend.isPending} onClick={() => resend.mutate(undefined)}>{t("shell.verifyResend")}</button>
    </div>
  );
}

/** ClientOS support, reading the workspace with the owner's leave: says so on every page. */
function SupportBanner({ name, until }: { name: string; until: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-ink px-4 py-1.5 text-center text-[13px] text-white">
      {t("shell.supportViewing", { name, until: dateTime(until) })}
      <button type="button" className="font-medium underline"
        onClick={async () => { await api.post("/v1/auth/logout").catch(() => {}); qc.clear(); void navigate({ to: "/login" }); }}>
        {t("shell.supportLeave")}
      </button>
    </div>
  );
}

/** An owner who let ClientOS support in sees it for as long as it lasts, and can end it. */
function SupportAccessBanner({ until }: { until: string }) {
  const { t } = useTranslation();
  const end = useAction(() => api.del("/v1/core/support-access"), { invalidate: [["me"], ["/v1/core/support-access"]], success: t("shell.supportEnded") });
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-info-soft px-4 py-1.5 text-center text-[13px] text-info">
      {t("shell.supportAllowed", { until: dateTime(until) })}
      <button type="button" className="font-medium underline" disabled={end.isPending} onClick={() => end.mutate(undefined)}>{t("shell.supportEnd")}</button>
    </div>
  );
}

function Banners() {
  const { data: me } = useMe();
  const { t } = useTranslation();
  const can = useCan();
  const online = useOnline();
  if (!me) return null;
  const items: ReactNode[] = [];
  if (!online) {
    items.push(<div key="off" role="status" className="flex items-center justify-center gap-3 bg-ink px-4 py-1.5 text-[13px] text-white">{t("shell.offlineBanner")}</div>);
  }
  if (me.support) items.push(<SupportBanner key="support" name={me.tenant.name} until={me.support.until} />);
  else if (me.support_access_until) items.push(<SupportAccessBanner key="support" until={me.support_access_until} />);
  // Whoever handles the subscription is pointed to where it's paid.
  const pay = (label: string) => can("core.billing.manage") && <Link to="/settings/subscription" className="font-medium underline">{label}</Link>;
  const soon = (at?: string | null, days = 7) => !!at && new Date(at).getTime() - Date.now() < days * 86400000;
  if (me.tenant.delete_after) {
    // Due to be deleted: read-only until then, and an owner can keep it from the subscription page.
    items.push(
      <div key="del" role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-danger px-4 py-2 text-center text-white">
        {t("shell.deletionDue", { date: date(me.tenant.delete_after) })}
        {pay(t("shell.keepWorkspace"))}
        {can("core.data.export") && <a href="/api/v1/core/export" download className="font-medium underline">{t("shell.downloadData")}</a>}
      </div>,
    );
  } else if (me.tenant.status === "suspended" || me.tenant.status === "cancelled") {
    items.push(
      // Read-only: the data still reads and can be taken away.
      <div key="s" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-danger px-4 py-2 text-center text-white">
        {can("core.billing.manage") ? t("shell.suspendedOwner") : t("shell.suspended")}
        {pay(t("shell.payNow"))}
        {can("core.data.export") && <a href="/api/v1/core/export" download className="font-medium underline">{t("shell.downloadData")}</a>}
      </div>,
    );
  } else if (me.tenant.status === "trial" && soon(me.tenant.trial_ends_at, 7)) {
    items.push(
      <div key="t" className="flex items-center justify-center gap-3 bg-saffron-soft px-4 py-1.5 text-[13px]">
        {t("shell.trialEnds", { date: date(me.tenant.trial_ends_at) })}
        {pay(t("shell.subscribe"))}
      </div>,
    );
  } else if (me.tenant.status === "active" && soon(me.tenant.paid_until)) {
    items.push(
      <div key="p" className="flex items-center justify-center gap-3 bg-saffron-soft px-4 py-1.5 text-[13px]">
        {t("shell.paidUntil", { date: date(me.tenant.paid_until) })}
        {pay(t("shell.payNow"))}
      </div>,
    );
  }
  // Reached by its old address: where it lives now.
  if (me.tenant.moved_to) {
    items.push(
      <div key="moved" role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-info-soft px-4 py-1.5 text-center text-[13px] text-info">
        {t("shell.movedTo")}
        <a href={me.tenant.moved_to} className="num font-medium underline" dir="ltr">{me.tenant.moved_to.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
      </div>,
    );
  }
  if (me.tenant.demo_back_url) {
    items.push(
      <div key="demo" role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-saffron-soft px-4 py-1.5 text-center text-[13px]">
        {t("demo.banner")}
        <DemoRoleSwitch back={me.tenant.demo_back_url} current={me.roles?.[0]} />
        <a href={me.tenant.demo_back_url} className="font-medium underline">{t("demo.back")}</a>
      </div>,
    );
  }
  items.push(<AnnouncementBanners key="announcements" />);
  // Until their email is confirmed, nothing is emailed for them.
  if (me.email_verified === false && !me.tenant.demo_back_url && !me.support) items.push(<VerifyBanner key="verify" email={me.email} />);
  // Two-step verification is a step in "Start here"; the banner comes only in the last
  // two days before it's required.
  if (!me.totp_enabled && me.totp_deadline && soon(me.totp_deadline, 2)) {
    items.push(
      <div key="2fa" className="flex items-center justify-center gap-3 bg-info-soft px-4 py-1.5 text-[13px] text-info">
        {t("shell.totpDue", { date: date(me.totp_deadline) })}
        <Link to="/settings/security" className="font-medium underline">{t("shell.enableNow")}</Link>
      </div>,
    );
  }
  return <>{items}</>;
}


/** The search everything starts from: a field at the top that opens the palette (Ctrl+K). */
function SearchField({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={onOpen}
      className="flex h-10 w-full max-w-[520px] items-center gap-2.5 rounded-full bg-paper px-4 text-start text-[14px] text-ink-3 transition-colors hover:bg-line-2">
      <Search className="size-[18px] shrink-0" aria-hidden />
      <span className="flex-1 truncate">{t("shell.searchHint")}</span>
      <kbd className="num rounded border border-line bg-surface px-1.5 text-[11px]">Ctrl K</kbd>
    </button>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(false);
  const [creating, setCreating] = useState(false);
  const [palette, setPalette] = useState(false);
  const [open, setOpen] = useRemembered("clientos.menu", true);
  const brand = useMe().data?.tenant.brand;
  const look = JSON.stringify(brand ?? null);
  useEffect(() => applyBrand(brand), [look]); // eslint-disable-line react-hooks/exhaustive-deps -- when the look changes, not on each refetch
  // What was logged with no signal (visits) goes out when the app opens, when the signal is
  // back, and each minute after a round a weak signal cut short.
  useEffect(() => {
    const send = () => void (navigator.onLine && flushOutbox((path, body) => api.post(path, body)).catch(() => {}));
    send();
    const every = window.setInterval(send, 60_000);
    window.addEventListener("online", send);
    return () => { window.clearInterval(every); window.removeEventListener("online", send); };
  }, []);
  useReportPages();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <AppStartProvider>
    <div className="flex min-h-dvh bg-surface">
      <aside className={clsx("sticky top-0 hidden h-dvh shrink-0 flex-col border-e border-line bg-paper transition-[width] duration-200 lg:flex", open ? "w-[248px]" : "w-[68px]")}>
        <div className={clsx("flex h-16 shrink-0 items-center", open ? "justify-between ps-6 pe-2" : "justify-center")}>
          {open ? <Link to="/" aria-label={t("nav.home")} className="min-w-0"><BrandMark brand={brand} className="h-[21px] w-auto" /></Link> : <BrandIcon brand={brand} />}
          {open && (
            <button type="button" className="btn-ghost p-2 text-ink-3" onClick={() => setOpen(false)} aria-label={t("shell.closeMenu")} title={t("shell.closeMenu")} aria-expanded>
              <PanelLeftClose className="size-4 rtl:-scale-x-100" />
            </button>
          )}
        </div>
        {open && <div className="px-3"><CompanySwitcher /></div>}
        <div className="min-h-0 flex-1"><Sidebar collapsed={!open} /></div>
        {!open && (
          <button type="button" className="btn-ghost mx-auto mb-4 p-2 text-ink-3" onClick={() => setOpen(true)} aria-label={t("shell.openMenu")} title={t("shell.openMenu")} aria-expanded={false}>
            <PanelLeftOpen className="size-4 rtl:-scale-x-100" />
          </button>
        )}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <Banners />
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-line-2 bg-surface/90 px-4 backdrop-blur sm:px-6 lg:px-10">
          <Link to="/" className="min-w-0 lg:hidden" aria-label={t("nav.home")}><BrandMark brand={brand} className="h-5 w-auto" /></Link>
          <div className="min-w-0 flex-1 max-lg:hidden"><SearchField onOpen={() => setPalette(true)} /></div>
          <div className="flex-1 lg:hidden" />
          <div className="max-lg:hidden"><NewMenu /></div>
          <TimerChip />
          <NotificationsBell />
          <UserMenu />
        </header>
        <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pt-5 pb-[calc(var(--bottom-bar)+1.5rem)] sm:px-6 lg:px-10 lg:pt-7">
          <Page onArea={() => setMenu(true)} />
        </main>
      </div>
      <BottomBar onSearch={() => setPalette(true)} onNew={() => setCreating(true)} onMenu={() => setMenu(true)} />
      <MenuDrawer open={menu} onClose={() => setMenu(false)} top={<CompanySwitcher />} />
      <NewSheet open={creating} onClose={() => setCreating(false)} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </div>
    </AppStartProvider>
  );
}
